/**
 * Shared SEO/AEO content — the single source of truth for the FAQ, used by
 * BOTH the visible on-page FAQ (`about-section.tsx`) and the `FAQPage`
 * JSON-LD (`layout.tsx`).
 *
 * Why one module: Google's FAQ rich-result policy requires the structured
 * data to match the FAQ content visible to users. Schema-only FAQs (not
 * rendered on the page) are ignored or treated as cloaking. Keeping the
 * questions/answers here and consuming them in both places guarantees the
 * visible copy and the schema never drift.
 */

export interface FaqItem {
	question: string
	answer: string
}

export const FAQ_ITEMS: readonly FaqItem[] = [
	{
		question: 'How accurate is the Fuel Estimator?',
		answer:
			'It combines live state-level fuel prices from the U.S. Energy Information Administration with elevation-adjusted MPG calculations derived from SRTM/NASADEM raster data. Grade adjustments apply per vehicle weight class, calibrated against peer-reviewed U.S. national-lab drive-cycle studies. It has not been formally validated against fleet data, so treat the output as a useful reference — actual variance depends on station-level price differences within a state, driving style, vehicle condition, and load weight.',
	},
	{
		question: 'What data sources does it use?',
		answer:
			'Fuel prices come from the U.S. EIA (updated weekly, state-level where available, PADD regional average otherwise). Routing comes from Mapbox. Elevation comes from Open-Meteo (SRTM/NASADEM raster) with Mapbox Tilequery as a fallback. State attribution is computed offline against bundled US state boundary polygons, so it keeps working even if upstream map services are slow.',
	},
	{
		question: 'Is it really free?',
		answer:
			"Yes — completely free, no signup, no account. It's a personal project built to give back to the touring community.",
	},
	{
		question: 'Can it handle tour buses and semi-trucks?',
		answer:
			'Yes. There are six vehicle presets tuned for touring use: sedan/SUV, 15-passenger van, Sprinter van, 26-foot box truck, Prevost tour bus, and full semi-truck. Each has its own base MPG, default fuel type (diesel or gas), and weight-class-aware grade penalty. You can also override the preset MPG with your real-world measured value.',
	},
	{
		question: 'Who built this?',
		answer:
			'It was built by Chris Lee Bergstrom, a tour manager of 20+ years, as a personal project to give back to the touring industry — he has seen firsthand how much fuel costs shape a tour budget.',
	},
]
